import { createSlice } from "@reduxjs/toolkit";
import {
  fetchPrescriptionLayout,
  savePrescriptionLayout,
} from "../thunks/prescriptionLayoutThunks";

const toKey = (v) => (v == null ? "" : String(v).trim());

const initialState = {
  data: null,          
  status: "idle",      
  error: null,
  saveStatus: "idle",  
  saveError: null,
  byDoctorId: {},        
  statusByDoctorId: {},  
  errorByDoctorId: {},   
};

const prescriptionLayoutSlice = createSlice({
  name: "prescriptionLayout",
  initialState,
  reducers: {
    resetPrescriptionLayoutState: () => initialState,

    clearPrescriptionLayoutForDoctor: (state, action) => {
      const doctorId = toKey(action.payload);
      if (!doctorId) return;
      delete state.byDoctorId[doctorId];
      delete state.statusByDoctorId[doctorId];
      delete state.errorByDoctorId[doctorId];
    },
  },
  extraReducers: (builder) => {
    /* ---------- FETCH TEMPLATE ---------- */
    builder
      .addCase(fetchPrescriptionLayout.pending, (state, action) => {
        const doctorId = toKey(action.meta?.arg); // fetchPrescriptionLayout(doctorId)

        state.status = "loading";
        state.error = null;
        if (doctorId) {
          state.statusByDoctorId[doctorId] = "loading";
          state.errorByDoctorId[doctorId] = null;
        }
      })
      .addCase(fetchPrescriptionLayout.fulfilled, (state, action) => {
        const doctorId = toKey(action.meta?.arg);
        const tpl = action.payload || null;
        state.status = "succeeded";
        state.data = tpl;

        if (doctorId) {
          state.byDoctorId[doctorId] = tpl;
          state.statusByDoctorId[doctorId] = "succeeded";
          state.errorByDoctorId[doctorId] = null;
        }
      })
      .addCase(fetchPrescriptionLayout.rejected, (state, action) => {
        const doctorId = toKey(action.meta?.arg);
        const err = action.payload || action.error?.message || "Erreur de chargement";
        state.status = "failed";
        state.error = err;
        if (doctorId) {
          state.statusByDoctorId[doctorId] = "failed";
          state.errorByDoctorId[doctorId] = err;
          if (state.byDoctorId[doctorId] === undefined) {
            state.byDoctorId[doctorId] = null;
          }
        }
      });

    /* ---------- SAVE TEMPLATE ---------- */
    builder
      .addCase(savePrescriptionLayout.pending, (state) => {
        state.saveStatus = "loading";
        state.saveError = null;
      })
      .addCase(savePrescriptionLayout.fulfilled, (state, action) => {
        state.saveStatus = "succeeded";

        const saved = action.payload || null;
        state.data = saved || state.data;

        const doctorId = toKey(saved?.doctorId || saved?.doctor?.id || saved?.doctor?.doctorId);

        if (doctorId) {
          state.byDoctorId[doctorId] = saved;
          state.statusByDoctorId[doctorId] = "succeeded";
          state.errorByDoctorId[doctorId] = null;
        }
      })
      .addCase(savePrescriptionLayout.rejected, (state, action) => {
        state.saveStatus = "failed";
        state.saveError =
          action.payload || action.error?.message || "Erreur de sauvegarde";
      });
  },
});

export const {
  resetPrescriptionLayoutState,
  clearPrescriptionLayoutForDoctor,
} = prescriptionLayoutSlice.actions;

export default prescriptionLayoutSlice.reducer;

/* ---------- Selectors (compat) ---------- */
export const selectPrescriptionLayout = (state) =>
  state.prescriptionLayout?.data || null;

export const selectPrescriptionLayoutStatus = (state) =>
  state.prescriptionLayout?.status || "idle";

export const selectPrescriptionLayoutError = (state) =>
  state.prescriptionLayout?.error || null;

export const selectPrescriptionLayoutSaveStatus = (state) =>
  state.prescriptionLayout?.saveStatus || "idle";

export const selectPrescriptionLayoutSaveError = (state) =>
  state.prescriptionLayout?.saveError || null;

/* ---------- ✅ NEW Selectors (par doctorId) ---------- */
export const selectPrescriptionLayoutByDoctorId = (state, doctorId) => {
  const id = toKey(doctorId);
  if (!id) return null;
  return state.prescriptionLayout?.byDoctorId?.[id] ?? null;
};

export const selectPrescriptionLayoutStatusByDoctorId = (state, doctorId) => {
  const id = toKey(doctorId);
  if (!id) return "idle";
  return state.prescriptionLayout?.statusByDoctorId?.[id] ?? "idle";
};

export const selectPrescriptionLayoutErrorByDoctorId = (state, doctorId) => {
  const id = toKey(doctorId);
  if (!id) return null;
  return state.prescriptionLayout?.errorByDoctorId?.[id] ?? null;
};
